import { useMutation } from "@tanstack/react-query";
import type * as React from "react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CfInfoDialog from "@/components/pop-ui/cf-info-dialog";
import { toast } from "@/components/pop-ui/pop-toast";
import { PasswordInput } from "@/components/ui/password-input";
import { authClient } from "@/lib/auth/auth-client";
import { handleAuthCall } from "@/lib/auth/auth-utils";
import { getAuthHeadersWaitSign } from "@/lib/auth/td-utils";
import { handleBusinessError } from "@/lib/error-handler";
import { mapWebLangToServerLang } from "@/lib/i18n/utils";
import { getCurrentLanguageInClient } from "@/lib/utils.client";
import { useGlobalConfig } from "@/providers/global-config-context";
import { useTurnstile } from "@/providers/turnstile-provider";
import { trackSignUp } from "@/track/gtm";
import { CodeInput, type CodeInputRef } from "../components/code-input";
import { Button } from "../components/form-items";
import { handleAuthApiError, type RegisterFormProps, type UserFormErrors, validatePassword } from "../utils";

// 邮箱注册表单
export function RegisterFormEmail({
  inputValue,
  area,
  onFinish
}: RegisterFormProps) {
  const {
    t
  } = useTranslation();
  const {
    globalConfig
  } = useGlobalConfig();
  const {
    isSdkReady,
    sdkError
  } = useTurnstile();
  const passwordId = useId();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [errors, setErrors] = useState<UserFormErrors>({
    email: "",
    phone: "",
    password: "",
    code: ""
  });
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const codeInputRef = useRef<CodeInputRef>(null);

  // Dialog open state for captcha (closed by default)
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false);
  const [isTurnstileProcessStarted, setIsTurnstileProcessStarted] = useState(false);

  // Handle Turnstile completion - use token immediately and clear it
  const handleTurnstileSuccess = (token: string) => {
    setCaptchaOpen(false);
    // Don't clear loading state here - let the API call handle it
    // Use token immediately for send code only
    sendVerificationCode(token);
  };

  // Handle turnstile dialog open - set loading state
  const handleTurnstileOpen = () => {
    // Check SDK readiness before opening dialog
    if (sdkError) {
      // SDK failed to load, show error and don't open dialog
      toast.error(t("gateway.error.475"));
      return;
    }
    if (!isSdkReady) {
      // SDK is still loading, do nothing (user can try again)
      return;
    }
    setIsTurnstileLoading(true);
    setIsTurnstileProcessStarted(true);
    setCaptchaOpen(true);
  };

  // Handle turnstile error - reset loading state
  const handleTurnstileError = () => {
    setIsTurnstileLoading(false);
    setIsTurnstileProcessStarted(false);
    setCaptchaOpen(false);
    // Show error message as toast for dialog verification failure
    toast.error(t("gateway.error.476"));
  };

  // 发送验证码mutation
  const {
    mutate: sendVerificationCode,
    isPending: isSendingCode
  } = useMutation({
    mutationFn: async (cfTurnstileToken: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set 30-second timeout to reset button
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        toast.error(t("toast.error.requestTimeout", "Request timeout"));
      }, 30000);
      try {
        // 获取包含 TD 参数的请求头（等待 sign 准备就绪）
        const requestHeaders = await getAuthHeadersWaitSign("email-register", {
          "x-captcha-response": cfTurnstileToken
        });
        const result = await handleAuthCall(authClient.emailOtp.sendVerificationOtp({
          email: inputValue,
          type: "sign-in",
          fetchOptions: {
            headers: requestHeaders
          }
        }));

        // Clear timeout on success
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return result;
      } catch (error) {
        // Clear timeout on error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        throw error;
      }
    },
    onSuccess: () => {
      setIsTurnstileLoading(false); // Clear loading state on success
      setIsTurnstileProcessStarted(false); // Reset process state on success - user can proceed normally
      // 显示验证码输入框
      setShowCodeInput(true);

      // 启动倒计时
      setTimeout(() => {
        codeInputRef.current?.startCountdown();
      }, 500);

      // 显示成功提示
      toast.success(t("toast.success.verificationCodeSentToYourEmail"));
    },
    onError: (error_0: {
      code?: string;
      message: string;
    }) => {
      setIsTurnstileLoading(false); // Clear loading state on error
      setIsTurnstileProcessStarted(false); // Reset process state on error
      // Reset Turnstile states on error
      setCaptchaOpen(false);
      handleAuthApiError(error_0, t("toast.error.sendCodeFailedTryLater"));
    }
  });

  // 注册mutation
  const {
    mutate: register,
    isPending: isRegistering
  } = useMutation({
    mutationFn: async () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set 30-second timeout to reset button
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        toast.error(t("toast.error.requestTimeout", "Request timeout"));
      }, 30000);
      try {
        // 获取包含 TD 参数的请求头（等待 sign 准备就绪）
        const requestHeaders_0 = await getAuthHeadersWaitSign("email-register", {
          "Content-Type": "application/json",
          "X-AREA": area,
          "Accept-Language": mapWebLangToServerLang(getCurrentLanguageInClient())
        });
        const response = await fetch(`${process.env.NEXT_PUBLIC_SERVER_URL}/api/auth/sign-in/email-otp`, {
          method: "POST",
          headers: requestHeaders_0,
          body: JSON.stringify({
            email: inputValue,
            otp: code,
            password
          }),
          credentials: "include"
        });
        if (!response.ok) {
          let errorData: {
            code?: string;
            message: string;
          } = {
            code: "",
            message: ""
          };
          try {
            errorData = await response.json();
          } catch (_e) {
            errorData = {
              message: response.statusText || t("error.apiRequestFailed")
            };
          }
          throw {
            code: errorData.code,
            message: errorData.message || t("error.unknownErrorDuringRegister")
          };
        }

        // Clear timeout on success
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return response.json();
      } catch (error_1) {
        // Clear timeout on error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        throw error_1;
      }
    },
    onSuccess: (data: {
      session?: {
        userId: string;
      };
    }) => {
      handleBusinessError(data);

      // 注册成功埋点
      if (data?.session?.userId) {
        trackSignUp({
          pop_user_id: data.session.userId,
          status: "success"
        });
      }
      if (onFinish) {
        onFinish();
      }
    },
    onError: (error_2: {
      code?: string;
      message: string;
    }) => {
      // Reset Turnstile states on error
      setCaptchaOpen(false);
      // 注册失败埋点
      trackSignUp({
        pop_user_id: "",
        // 失败时没有用户ID
        status: "failed"
      });
      handleAuthApiError(error_2, t("toast.error.registerFailedTryLater"));
    }
  });

  // 处理注册发送验证码
  const handleSendCode = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault?.();

    // Prevent re-submission if turnstile process has already started
    if (isTurnstileProcessStarted) {
      return;
    }

    // Reset timeout state when starting new send code attempt
    setIsTimedOut(false);

    // 清除错误
    setErrors({
      email: "",
      phone: "",
      password: "",
      code: ""
    });

    // 校验密码
    const passwordError = validatePassword(password, t);
    if (passwordError) {
      setErrors(prev => ({
        ...prev,
        password: passwordError
      }));
      return;
    }

    // If Turnstile is globally disabled, proceed directly with sending code
    if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
      sendVerificationCode("");
      return;
    }

    // Open captcha dialog - the dialog will handle turnstile logic internally
    handleTurnstileOpen();
  };

  // 处理注册提交
  const handleRegister = async (e_0?: React.FormEvent<HTMLFormElement>) => {
    e_0?.preventDefault?.();

    // Reset timeout state when starting new register attempt
    setIsTimedOut(false);

    // 校验密码
    const passwordError_0 = validatePassword(password, t);
    if (passwordError_0) {
      setErrors(prev_0 => ({
        ...prev_0,
        password: passwordError_0
      }));
      return;
    }
    // 验证验证码
    if (!code) {
      toast.error(t("toast.error.enterVerificationCode"));
      return;
    } else if (code.length !== 6) {
      toast.error(t("toast.error.enter6DigitVerificationCode"));
      return;
    }

    // No Turnstile required for account creation - proceed directly
    register();
  };
  const isLoading = isSendingCode || isRegistering;
  const isTurnstileEnabled = globalConfig?.PUBLIC_TURNSTILE_ENABLED;

  // Determine if button should be in loading state
  const isButtonLoading = isLoading && !isTimedOut || isTurnstileEnabled && isTurnstileLoading;
  return <form onSubmit={showCodeInput ? handleRegister : handleSendCode} className="space-y-3">
			<div className="space-y-2">
				<span className="text-foreground flex h-10 md:h-12 items-center bg-gray-100 px-3 py-2 text-xs! md:text-base! break-all opacity-50">
					{inputValue}
				</span>
			</div>

			<div className="space-y-2">
				<PasswordInput id={passwordId} placeholder={t("placeholder.createPassword")} value={password} onChange={e_1 => setPassword(e_1.target.value)} disabled={isLoading} error={errors.password} />
			</div>

			<CfInfoDialog open={captchaOpen} onOpenChange={setCaptchaOpen} containerId="cf-turnstile-container-register-dialog" onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={handleTurnstileError} />

			{showCodeInput && <CodeInput ref={codeInputRef} value={code} onChange={setCode} onResend={() => handleSendCode()} error={errors.code} disabled={isLoading} autoStartCountdown={false} />}

			<Button type="submit" className="w-full" size="lg" loading={isButtonLoading}>
				{isButtonLoading ? t("button.registering") : t("button.createMyAccount")}
			</Button>
		</form>;
}